import React, { useContext, useState } from 'react';
import {
  SelectField,
  InputField,
  CheckboxField
} from '@trp-beacon-ui-kit/react-controls';
import { FlowContext } from '../../core/flow-engine';
import PhoneNumber from '../../components/PhoneNumber/PhoneNumber';
import AdditionalPhoneNumberFields from './AdditionalPhoneNumberFields';
import { states, getDefaultMobilePhone } from './utils';
import {
  accountTypes,
  provideDefault,
  handleInputFormat
} from '../../flows/shared/utils';
import {
  addressLine1Validation,
  poBoxLine1Validation,
  addressLine2Validation,
  poBoxLine2Validation,
  valueRequired,
  phoneValidation,
  emailValidation,
  zipCodeValidation,
  cityValidation,
  stateValidation,
  mailingCityValidation,
  mailingStateValidation
} from '../../flows/shared/validations';
import { MFBeaconButton } from '../../components/Beacon/BeaconComponents';
import styles from './styles.css';
import Heading from '../../components/Typography/Heading/Heading';
import JointOwnerCISection from '../../components/JointOwnerCISection/JointOwnerCISection';
import MinorCISection from '../../components/MinorCISection/MinorCISection';
import { withDefault } from '../../core/util';
import { generateGeneralTestID } from '../../generateAutomationTestIDs';

const ContactInformationScreen = () => {
  const { back, next, formProps, appState } = useContext(FlowContext);
  const [mailingAddressDiffValue, setMailingAddressDiffValue] = useState(
    appState?.store?.mailingAddressDifferent
  );

  const {
    store: { accountType, app, prospect }
  } = appState;

  const {
    customer: {
      residentialAddress = {},
      mailingAddress = {},
      primaryEmailAddress,
      phones
    }
  } = app;

  const phone1 = getDefaultMobilePhone(phones);

  const { street1, street2, city, state, zip } = residentialAddress;
  const {
    street1: mailingStreet1,
    street2: mailingStreet2,
    city: mailingCity,
    state: mailingState,
    zip: mailingZip
  } = mailingAddress;

  const toggleMailingAddressDifferent = () =>
    setMailingAddressDiffValue(!mailingAddressDiffValue);

  const [primaryState, setPrimaryState] = useState(state);
  const [phoneValue, setPhoneValue] = useState('');

  const changePrimaryState = ({ target: { value } }) => {
    return setPrimaryState(value);
  };

  const plaidSuccess = Boolean(
    appState?.store?.loadedData?.plaidResult?.success
  );

  return (
    <div className="contact-information">
      <div className="title-wrapper">
        <Heading text="Enter your remaining contact information" />
      </div>

      <div className="address-fields">
        <div className="address-line1">
          <InputField
            title="Residential / Legal Address Line 1"
            {...formProps.residentialStreet1}
            data-name={generateGeneralTestID(
              'ContactInformation',
              'AccountOwnerResidentialStreetOne'
            )}
            defaultValue={provideDefault(street1, formProps.residentialStreet1)}
            validation={[
              addressLine1Validation,
              poBoxLine1Validation,
              valueRequired
            ]}
            disabled={plaidSuccess}
            maxLength="35"
            className="blocked_data"
          />
        </div>

        <div className="address-line2">
          <InputField
            title="Residential / Legal Address Line 2"
            {...formProps.residentialStreet2}
            data-name={generateGeneralTestID(
              'ContactInformation',
              'AccountOwnerResidentialStreetTwo'
            )}
            defaultValue={provideDefault(street2, formProps.residentialStreet2)}
            validation={[addressLine2Validation, poBoxLine2Validation]}
            disabled={plaidSuccess}
            maxLength="35"
            className="blocked_data"
          />
        </div>
      </div>
      <div className="address-fields">
        <div className="city-input">
          <InputField
            title="City"
            {...formProps.residentialCity}
            data-name={generateGeneralTestID(
              'ContactInformation',
              'AccountOwnerResidentialCity'
            )}
            defaultValue={provideDefault(city, formProps.residentialCity)}
            disabled={plaidSuccess}
            validation={[cityValidation, valueRequired]}
            className="blocked_data"
          />
        </div>
        <div className="zipcode-input">
          <InputField
            title="Zip Code"
            {...formProps.residentialZip}
            data-name={generateGeneralTestID(
              'ContactInformation',
              'AccountOwnerResidentialZip'
            )}
            defaultValue={provideDefault(zip, formProps.residentialZip)}
            validation={[zipCodeValidation, valueRequired]}
            disabled={plaidSuccess}
            maxLength="5"
            className="blocked_data"
          />
        </div>
        <div className="state-input">
          <SelectField
            title="State"
            {...formProps.residentialState}
            data-name={generateGeneralTestID(
              'ContactInformation',
              'AccountOwnerResidentialState'
            )}
            defaultValue={provideDefault(state, formProps.residentialState)}
            validation={[stateValidation, valueRequired]}
            disabled={plaidSuccess}
            onChange={changePrimaryState}>
            <option hidden value="">
              State
            </option>
            {states.map(abrr => {
              return (
                <option
                  key={abrr}
                  value={abrr}
                  data-name={generateGeneralTestID(
                    'ContactInformation',
                    `SelectResidentialState_${abrr}`
                  )}>
                  {abrr}
                </option>
              );
            })}
          </SelectField>
        </div>
      </div>

      <CheckboxField
        name="mailingAddressDifferent"
        data-name={generateGeneralTestID(
          'ContactInformation',
          'DifferentMailingAddressCheckbox'
        )}
        title="My mailing address is different from my residential/legal address"
        onClick={toggleMailingAddressDifferent}
        checked={mailingAddressDiffValue}
        {...formProps.mailingAddressDifferent}
      />

      {mailingAddressDiffValue && (
        <div>
          <div className="address-fields">
            <div className="address-line1">
              <InputField
                title="Mailing Address"
                {...formProps.mailingStreet1}
                data-name={generateGeneralTestID(
                  'ContactInformation',
                  'AccountOwnerMailingStreetOne'
                )}
                defaultValue={provideDefault(
                  mailingStreet1,
                  formProps.mailingStreet1
                )}
                validation={addressLine1Validation}
                maxLength="35"
                className="blocked_data"
              />
            </div>
            <div className="address-line2">
              <InputField
                title="Address Line 2"
                {...formProps.mailingStreet2}
                data-name={generateGeneralTestID(
                  'ContactInformation',
                  'AccountOwnerMailingStreetTwo'
                )}
                defaultValue={provideDefault(
                  mailingStreet2,
                  formProps.mailingStreet2
                )}
                validation={addressLine2Validation}
                maxLength="35"
                className="blocked_data"
              />
            </div>
          </div>
          <div className="address-fields">
            <div className="city-input">
              <InputField
                title="City"
                {...formProps.mailingCity}
                data-name={generateGeneralTestID(
                  'ContactInformation',
                  'AccountOwnerMailingCity'
                )}
                defaultValue={provideDefault(
                  mailingCity,
                  formProps.mailingCity
                )}
                validation={mailingCityValidation}
                className="blocked_data"
              />
            </div>
            <div className="zipcode-input">
              <InputField
                title="Zip Code"
                {...formProps.mailingZip}
                data-name={generateGeneralTestID(
                  'ContactInformation',
                  'AccountOwnerMailingZip'
                )}
                defaultValue={provideDefault(mailingZip, formProps.mailingZip)}
                validation={zipCodeValidation}
                maxLength="5"
                className="blocked_data"
              />
            </div>
            <div className="state-input">
              <SelectField
                title="State"
                {...formProps.mailingState}
                data-name={generateGeneralTestID(
                  'ContactInformation',
                  'AccountOwnerMailingState'
                )}
                defaultValue={provideDefault(
                  mailingState,
                  formProps.mailingState
                )}
                validation={mailingStateValidation}>
                <option hidden value="">
                  State
                </option>
                {states.map(abrr => {
                  return (
                    <option
                      key={abrr}
                      value={abrr}
                      data-name={generateGeneralTestID(
                        'ContactInformation',
                        `SelectMailingState_${abrr}`
                      )}>
                      {abrr}
                    </option>
                  );
                })}
              </SelectField>
            </div>
          </div>
        </div>
      )}
      <div className="contact-fields">
        <div className="phone-input">
          <InputField
            title="Mobile phone"
            placeholder="(000) 000-0000"
            onChange={evt => handleInputFormat(evt, 'PHONE', setPhoneValue)}
            maxLength="14"
            autocomplete="new-password"
            validation={phoneValidation}
            {...formProps.phone1}
            data-name={generateGeneralTestID(
              'ContactInformation',
              'AccountOwnerMobilePhoneNumber'
            )}
            value={withDefault(phoneValue, phone1)}
            className="blocked_data"
          />
        </div>

        <div className="email-input">
          <InputField
            title="Email"
            {...formProps.primaryEmailAddress}
            data-name={generateGeneralTestID(
              'ContactInformation',
              'AccountOwnerEmail'
            )}
            defaultValue={provideDefault(
              primaryEmailAddress,
              formProps.primaryEmailAddress
            )}
            disabled={
              prospect
            } /* disable email fields for prospect due to email verification */
            validation={[emailValidation, valueRequired]}
            className="blocked_data"
          />
        </div>
      </div>

      <AdditionalPhoneNumberFields formProps={formProps} appState={appState} />

      {[accountTypes.JOINT].includes(accountType) && <JointOwnerCISection />}

      {[accountTypes.UGMAUTMA].includes(accountType) && (
        <MinorCISection primaryState={primaryState} />
      )}

      <div className="button-container">
        <MFBeaconButton
          name="next-button"
          id="contactInformationContinue"
          data-name={generateGeneralTestID(
            'ContactInformation',
            'ContinueButton'
          )}
          className="continue-btn"
          variant="primary"
          size="normal"
          onClick={next({
            section: 'addressValidation',
            mailingAddressDiffValue
          })}
          label="Continue"
        />
        <MFBeaconButton
          id="contactInformationBack"
          data-name={generateGeneralTestID('ContactInformation', 'BackButton')}
          variant="secondary"
          className="beacon-button-margin-override"
          size="normal"
          onClick={back()}
          label="Back"
        />
      </div>
      <PhoneNumber />
      <style jsx>{styles}</style>
    </div>
  );
};

export default ContactInformationScreen;
